import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper, throwFetchError } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { ShareTemplatesResponse, ShareTemplateResponse, PresetListResponse } from 'src/types/TShare';
import { listTemplates, getTemplate, getDefaultTemplate, getPresetTemplates } from '../functions/templateApi';

/**
 * Hook to fetch the list of templates for the authenticated user
 */
export function useTemplatesListQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareTemplatesResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => listTemplates(accessToken)),
    [accessToken],
  );

  const query = useQuery<ShareTemplatesResponse, FetchError>(
    [queryNames.shareTemplate, 'list'],
    queryFn,
    {
      ...actualQueryOptions,
      staleTime: 60000, // 1 minute
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

/**
 * Hook to fetch a single template by ID
 */
export function useTemplateQuery(
  templateId?: string,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareTemplateResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getTemplate(accessToken, templateId!)),
    [accessToken, templateId],
  );

  const query = useQuery<ShareTemplateResponse, FetchError>(
    [queryNames.shareTemplate, templateId],
    queryFn,
    {
      ...actualQueryOptions,
      enabled: !!templateId,
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

/**
 * Hook to fetch the user's default template
 */
export function useDefaultTemplateQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ShareTemplateResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getDefaultTemplate(accessToken)),
    [accessToken],
  );

  const query = useQuery<ShareTemplateResponse, FetchError>(
    [queryNames.shareTemplate, 'default'],
    queryFn,
    {
      ...actualQueryOptions,
      staleTime: 60000, // 1 minute
    },
  );

  // Don't throw error for default template - it may not exist
  return query;
}

/**
 * Hook to fetch available preset templates catalog
 */
export function usePresetTemplatesQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<PresetListResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getPresetTemplates(accessToken)),
    [accessToken],
  );

  const query = useQuery<PresetListResponse, FetchError>(
    [queryNames.shareTemplate, 'presets'],
    queryFn,
    {
      ...actualQueryOptions,
      staleTime: 300000, // 5 minutes - presets don't change often
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (!query.isRefetchError) {
      throwFetchError(query.error);
    }
  }

  return query;
}

